import React, { useLayoutEffect, useRef, useState } from 'react';
import { Relic, RELIC_MAIN } from './runeEngine';

/**
 * **유물 — 게임 룬 창 오른쪽의 육각 유물 칸과, 눌렀을 때의 창.**
 *
 * 몹에 새긴 유물은 하나다. 그림은 게임 화면에서 잘라 왔고(사용자가 보내 준 네 장), **강화도에 따라
 * 달라진다**: +1~3 흰 톱니, +4~8 파란 보석, +9~11 보라 보석, +12~15 붉은 보석. 창에는 "+8 유물",
 * 주효과(공격력 +11%) 크게, 내구도 한 줄. 부효과는 이름표가 없어 번호로만 나와서 뺐다(사용자 지시) —
 * 전투력에도 안 들어간다. 유물 종류의 이름표도 아직 못 구해 "종류 N" 으로 적는다.
 */

const OUTLINE = '-1px 0 #1a0f08, 1px 0 #1a0f08, 0 -1px #1a0f08, 0 1px #1a0f08';
const text = (size: number, color: string, weight = 500): React.CSSProperties => ({ fontSize: size, color, fontWeight: weight, textShadow: OUTLINE });

/** 강화도 → 그림. 게임이 세 칸(+3·+8·+11)마다 모양을 바꾼다 */
export function relicImage(level: number): string {
    const tier = level >= 12 ? 4 : level >= 9 ? 3 : level >= 4 ? 2 : 1;
    return `/img/relics/tier${tier}.webp`;
}

/** 육각 유물 칸 — 별 오른쪽에 하나 */
export function RelicBadge({ relic, size, picked, onClick }: { relic: Relic | null; size: number; picked?: boolean; onClick?: () => void }) {
    return (
        <div
            role={onClick && relic ? 'button' : undefined}
            onClick={relic ? onClick : undefined}
            title={relic ? `+${relic.level} 유물 · ${RELIC_MAIN[relic.main[0]] || ''} +${relic.main[1]}%` : '유물 없음'}
            style={{ position: 'relative', width: size, height: size, flexShrink: 0, cursor: onClick && relic ? 'pointer' : 'default', opacity: relic ? 1 : 0.55 }}
        >
            {relic ? (
                <img loading="lazy"
                    src={relicImage(relic.level)} alt=""
                    style={{
                        width: '100%', height: '100%', objectFit: 'contain',
                        filter: picked ? 'drop-shadow(0 0 5px rgba(255, 214, 102, 0.95))' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.6))'
                    }}
                />
            ) : (
                <svg viewBox="0 0 100 100" width={size} height={size} style={{ overflow: 'visible' }}>
                    <polygon points="50,3 91,26.5 91,73.5 50,97 9,73.5 9,26.5" fill="#2a1a12" stroke="#5a3d24" strokeWidth={3} strokeLinejoin="round" />
                </svg>
            )}
            {relic && (
                // 육각 안쪽 오른아래 — 예전 자리(아래 4%)는 육각 아래 모서리에 걸쳐 처져 보였다(사용자 지시로 올림)
                <span style={{ position: 'absolute', right: size * 0.14, bottom: size * 0.18, lineHeight: 1, ...text(size * 0.19, '#fff', 600) }}>+{relic.level}</span>
            )}
        </div>
    );
}

/** 게임 창의 폭 — 아래 치수는 이 폭에서 잰 px 이고, 실제 폭에 맞춰 통째로 줄인다 (룬 창과 같다) */
const GAME_W = 1026;

/** 유물 창 — 룬 창 자리에 그린다 (바깥 판 바탕을 같이 쓴다) */
export default function RelicDetail({ relic, isMobile, onClose, width }: { relic: Relic; isMobile: boolean; onClose?: () => void; width?: number }) {
    const box = useRef<HTMLDivElement | null>(null);
    const [measured, setMeasured] = useState<number>(width || (isMobile ? 200 : 380));
    useLayoutEffect(() => {
        if (width || !box.current) return;
        const el = box.current;
        const read = () => setMeasured(el.clientWidth || (isMobile ? 200 : 380));
        read();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [width, isMobile]);
    const f = Math.max(0.16, Math.min(1, measured / GAME_W));
    return (
        <div ref={box} style={{ color: '#f4efe6', width: '100%', boxSizing: 'border-box', textAlign: 'left' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `${44 * f}px 1fr ${44 * f}px`, alignItems: 'center' }}>
                <span />
                <div style={{ textAlign: 'center', whiteSpace: 'nowrap', ...text(Math.max(12, 30 * f), '#f2b23a', 600) }}>+{relic.level} 유물 (종류 {relic.type})</div>
                <button onClick={onClose} title="닫기" style={{ justifySelf: 'end', width: 40 * f, height: 40 * f, border: 'none', background: 'transparent', color: '#e9c98a', fontSize: 34 * f, fontWeight: 900, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 14 * f, alignItems: 'center', marginTop: 8 * f }}>
                <RelicBadge relic={relic} size={110 * f} />
                <div style={{ minWidth: 0 }}>
                    <div style={{ whiteSpace: 'nowrap', ...text(Math.max(14, 40 * f), '#fff') }}>{RELIC_MAIN[relic.main[0]] || '?'} +{relic.main[1]}%</div>
                    <div style={{ marginTop: 4 * f, ...text(Math.max(10, 20 * f), '#b8a58c') }}>내구도 {relic.durability}</div>
                </div>
            </div>
        </div>
    );
}
